<template>
  <div class="ea-card d-flex flex-column" :class="cardClasses" :style="cardStyles">
    <div v-if="title || $slots.title" class="ea-card__title">
      <slot name="title">{{ title }}</slot>
    </div>

    <div v-if="content" class="ea-card__content">{{ content }}</div>
    <slot />
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'Card',
  props: {
    title: {
      type: String,
      default: null
    },
    content: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'compact', 'spacious'].includes(value)
    },
    elevation: {
      type: [String, Number],
      default: '0'
    },
    noBorder: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardClasses() {
      const classes = [`ea-card--${this.variant}`];

      if (this.elevation !== '0') {
        classes.push(`ea-card--elevation-${this.elevation}`);
      }

      if (this.noBorder) {
        classes.push('ea-card--no-border');
      }

      if (this.noPadding || this.noBorder) {
        classes.push('ea-card--no-border-radius');
      }

      // Default padding classes based on variant (skip if noPadding or noBorder is true)
      if (!this.noPadding && !this.noBorder) {
        switch (this.variant) {
          case 'compact':
            classes.push('pa-3');
            break;
          case 'spacious':
            classes.push('pa-6');
            break;
          default:
            classes.push('pa-5');
        }
      }

      return classes;
    },
    cardStyles() {
      return {
        // Custom styles can be added here if needed
        gap: '16px'
      };
    }
  }
});
</script>

<style scoped>
.ea-card {
  border: 1px solid var(--asu-grayscale-gray-4);
  border-radius: 12px;
  background-color: white;
}

.ea-card--no-border {
  border: none;
}

.ea-card--no-border-radius {
  border-radius: 0;
}

.ea-card--elevation-1 {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.ea-card--elevation-2 {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.ea-card--elevation-3 {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.ea-card__title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.ea-card__content {
  line-height: 1.5;
  font-weight: 500;
}

@media (max-width: 600px) {
  .ea-card--compact {
    width: calc(100vw - 32px);
  }

  .ea-card--spacious {
    width: calc(100vw - 64px);
  }
}
</style>
